import { useMutation, useQuery } from '@tanstack/react-query'
import { axios } from 'apis/axios'
import { ExtractFnReturnType, QueryConfig } from 'apis/queryConfig'
import {
  IManagedProject,
  IManagedProjectPaginated,
  IManagedSubProject,
  IManagedSubProjectPaginated,
} from 'types/project'

type ListResponse<T> = {
  results?: T[]
  data?: T[]
}

const getListPayload = <T,>(res: ListResponse<T> | T[]): T[] => {
  if (Array.isArray(res)) {
    return res
  }
  return res.results || res.data || []
}

export const getStaffProjectsManage = (): Promise<IManagedProject[]> => {
  return axios.get('oe/staff/projects/manage').then((res) => getListPayload<IManagedProject>(res.data))
}

export const getStaffProjectsManageTable = (
  params?: Record<string, any>
): Promise<IManagedProjectPaginated> => {
  return axios.get('oe/staff/projects/manage/table', { params }).then((res) => ({ data: res.data.results || [], ...res.data }))
}

export const getStaffProjectManageDetail = (id: number): Promise<IManagedProject> => {
  return axios.get(`oe/staff/projects/manage/${id}`).then((res) => res.data)
}

type CreateProjectPayload = {
  name: string
  shortcode?: string
  latitude: string
  longitude: string
  map_zoom: string
  logo: File
}

export const createStaffProject = (body: CreateProjectPayload): Promise<IManagedProject> => {
  const payload = new FormData()
  payload.append('name', body.name)
  if (body.shortcode) payload.append('shortcode', body.shortcode)
  payload.append('latitude', body.latitude)
  payload.append('longitude', body.longitude)
  payload.append('map_zoom', body.map_zoom)
  payload.append('logo', body.logo)

  return axios.post('oe/staff/projects/manage', payload).then((res) => res.data)
}

type UpdateProjectPayload = {
  id: number
  name?: string
  shortcode?: string
  latitude?: string
  longitude?: string
  map_zoom?: string
  logo?: File
  status?: 'Active' | 'Inactive'
}

export const updateStaffProject = ({ id, ...body }: UpdateProjectPayload): Promise<IManagedProject> => {
  const payload = new FormData()
  if (body.name !== undefined) payload.append('name', body.name)
  if (body.shortcode !== undefined) payload.append('shortcode', body.shortcode)
  if (body.latitude !== undefined) payload.append('latitude', body.latitude)
  if (body.longitude !== undefined) payload.append('longitude', body.longitude)
  if (body.map_zoom !== undefined) payload.append('map_zoom', body.map_zoom)
  if (body.logo) payload.append('logo', body.logo)
  if (body.status !== undefined) payload.append('status', body.status)

  return axios.patch(`oe/staff/projects/manage/${id}`, payload).then((res) => res.data)
}

export const deleteStaffProject = (id: number): Promise<void> => {
  return axios.delete(`oe/staff/projects/manage/${id}`).then(() => undefined)
}

export const getStaffSubProjectsManage = (parentId: number): Promise<IManagedSubProject[]> => {
  return axios
    .get(`oe/staff/project/${parentId}/subprojects/manage`)
    .then((res) => getListPayload<IManagedSubProject>(res.data))
}

export const getStaffSubProjectManageDetail = (
  parentId: number,
  id: number
): Promise<IManagedSubProject> => {
  return axios.get(`oe/staff/project/${parentId}/subprojects/manage/${id}`).then((res) => res.data)
}

type CreateSubprojectPayload = {
  parentId: number
  name: string
  shortcode: string
  latitude: string
  longitude: string
  logo?: File
  operation_type?: string
  cluster?: number | null
}

export const createStaffSubproject = ({ parentId, ...body }: CreateSubprojectPayload): Promise<IManagedSubProject> => {
  const payload = new FormData()
  payload.append('name', body.name)
  payload.append('shortcode', body.shortcode)
  payload.append('latitude', body.latitude)
  payload.append('longitude', body.longitude)
  if (body.logo) payload.append('logo', body.logo)
  if (body.operation_type) payload.append('operation_type', body.operation_type)
  if (body.cluster) payload.append('cluster', String(body.cluster))

  return axios.post(`oe/staff/project/${parentId}/subprojects/manage`, payload).then((res) => res.data)
}

type UpdateSubprojectPayload = {
  parentId: number
  id: number
  name?: string
  shortcode?: string
  latitude?: string
  longitude?: string
  logo?: File
  operation_type?: string | null
  cluster?: number | null
}

export const updateStaffSubproject = ({ parentId, id, ...body }: UpdateSubprojectPayload): Promise<IManagedSubProject> => {
  const payload = new FormData()
  if (body.name !== undefined) payload.append('name', body.name)
  if (body.shortcode !== undefined) payload.append('shortcode', body.shortcode)
  if (body.latitude !== undefined) payload.append('latitude', body.latitude)
  if (body.longitude !== undefined) payload.append('longitude', body.longitude)
  if (body.logo) payload.append('logo', body.logo)
  
  // Django's FormData parser crashes if we send an empty string `""` for integer fields or choice fields.
  // We send an empty string only if the user explicitly cleared the value (so backend can nullify it).
  if (body.operation_type !== undefined) {
    if (body.operation_type) payload.append('operation_type', body.operation_type)
    else payload.append('operation_type', '')
  }
  
  if (body.cluster !== undefined) {
    if (body.cluster) payload.append('cluster', String(body.cluster))
    else payload.append('cluster', '')
  }

  return axios.patch(`oe/staff/project/${parentId}/subprojects/manage/${id}`, payload).then((res) => res.data)
}

export const deleteStaffSubproject = ({ parentId, id }: { parentId: number; id: number }): Promise<void> => {
  return axios.delete(`oe/staff/project/${parentId}/subprojects/manage/${id}`).then(() => undefined)
}

// --- Clusters list ---

export type ClusterOption = { id: number; name: string }

export const getStaffClusters = (): Promise<ClusterOption[]> => {
  return axios.get('oe/staff/clusters').then((res) => res.data)
}

type GetClustersFnType = typeof getStaffClusters
export const useStaffClusters = (config?: QueryConfig<GetClustersFnType>) => {
  return useQuery<ExtractFnReturnType<GetClustersFnType>>({
    queryKey: ['staff-clusters'],
    queryFn: () => getStaffClusters(),
    ...config,
  })
}

type GetProjectsFnType = typeof getStaffProjectsManage
export const useStaffProjectsManage = (config?: QueryConfig<GetProjectsFnType>) => {
  return useQuery<ExtractFnReturnType<GetProjectsFnType>>({
    queryKey: ['staff-projects-manage'],
    queryFn: () => getStaffProjectsManage(),
    ...config,
  })
}

type GetProjectsTableFnType = typeof getStaffProjectsManageTable
export const useStaffProjectsManageTable = (
  params: Record<string, any>,
  config?: QueryConfig<GetProjectsTableFnType>
) => {
  return useQuery<ExtractFnReturnType<GetProjectsTableFnType>>({
    queryKey: ['staff-projects-manage-table', JSON.stringify(params)],
    queryFn: () => getStaffProjectsManageTable(params),
    ...config,
  })
}

type GetProjectDetailFnType = typeof getStaffProjectManageDetail
export const useStaffProjectManageDetail = (
  id: number,
  config?: QueryConfig<GetProjectDetailFnType>
) => {
  return useQuery<ExtractFnReturnType<GetProjectDetailFnType>>({
    queryKey: ['staff-project-manage-detail', id],
    queryFn: () => getStaffProjectManageDetail(id),
    enabled: !!id,
    ...config,
  })
}

type GetSubprojectsFnType = typeof getStaffSubProjectsManage
export const useStaffSubProjectsManage = (parentId: number, config?: QueryConfig<GetSubprojectsFnType>) => {
  return useQuery<ExtractFnReturnType<GetSubprojectsFnType>>({
    queryKey: ['staff-subprojects-manage', parentId],
    queryFn: () => getStaffSubProjectsManage(parentId),
    enabled: !!parentId,
    ...config,
  })
}

type GetSubprojectDetailFnType = typeof getStaffSubProjectManageDetail
export const useStaffSubProjectManageDetail = (
  parentId: number,
  id: number,
  config?: QueryConfig<GetSubprojectDetailFnType>
) => {
  return useQuery<ExtractFnReturnType<GetSubprojectDetailFnType>>({
    queryKey: ['staff-subproject-manage-detail', parentId, id],
    queryFn: () => getStaffSubProjectManageDetail(parentId, id),
    enabled: !!parentId && !!id,
    ...config,
  })
}

type CreateProjectFnType = typeof createStaffProject
export const useCreateStaffProject = () => {
  return useMutation<ExtractFnReturnType<CreateProjectFnType>, Error, Parameters<CreateProjectFnType>[0]>({
    mutationKey: ['staff-project-create'],
    mutationFn: (params) => createStaffProject(params),
  })
}

type UpdateProjectFnType = typeof updateStaffProject
export const useUpdateStaffProject = () => {
  return useMutation<ExtractFnReturnType<UpdateProjectFnType>, Error, Parameters<UpdateProjectFnType>[0]>({
    mutationKey: ['staff-project-update'],
    mutationFn: (params) => updateStaffProject(params),
  })
}

type DeleteProjectFnType = typeof deleteStaffProject
export const useDeleteStaffProject = () => {
  return useMutation<ExtractFnReturnType<DeleteProjectFnType>, Error, Parameters<DeleteProjectFnType>[0]>({
    mutationKey: ['staff-project-delete'],
    mutationFn: (id) => deleteStaffProject(id),
  })
}

type CreateSubprojectFnType = typeof createStaffSubproject
export const useCreateStaffSubproject = () => {
  return useMutation<ExtractFnReturnType<CreateSubprojectFnType>, Error, Parameters<CreateSubprojectFnType>[0]>({
    mutationKey: ['staff-subproject-create'],
    mutationFn: (params) => createStaffSubproject(params),
  })
}

type UpdateSubprojectFnType = typeof updateStaffSubproject
export const useUpdateStaffSubproject = () => {
  return useMutation<ExtractFnReturnType<UpdateSubprojectFnType>, Error, Parameters<UpdateSubprojectFnType>[0]>({
    mutationKey: ['staff-subproject-update'],
    mutationFn: (params) => updateStaffSubproject(params),
  })
}

type DeleteSubprojectFnType = typeof deleteStaffSubproject
export const useDeleteStaffSubproject = () => {
  return useMutation<ExtractFnReturnType<DeleteSubprojectFnType>, Error, Parameters<DeleteSubprojectFnType>[0]>({
    mutationKey: ['staff-subproject-delete'],
    mutationFn: (params) => deleteStaffSubproject(params),
  })
}

// --- Paginated subproject listing ---

type GetSubprojectsPaginatedParams = {
  parentId: number
  params?: Record<string, any>
}

export const getStaffSubProjectsPaginated = ({
  parentId,
  params,
}: GetSubprojectsPaginatedParams): Promise<IManagedSubProjectPaginated> => {
  return axios
    .get(`oe/staff/project/${parentId}/subprojects/manage`, { params })
    .then((res) => res.data)
}

type GetSubprojectsPaginatedFnType = typeof getStaffSubProjectsPaginated
export const useStaffSubProjectsPaginated = (
  parentId: number,
  params: Record<string, any>,
  config?: QueryConfig<GetSubprojectsPaginatedFnType>
) => {
  return useQuery<ExtractFnReturnType<GetSubprojectsPaginatedFnType>>({
    queryKey: ['staff-subprojects-manage', parentId, JSON.stringify(params)],
    queryFn: () => getStaffSubProjectsPaginated({ parentId, params }),
    enabled: !!parentId,
    ...config,
  })
}

// --- Shortcode validation ---

export const validateShortcode = async (
  shortcode: string,
  excludeId?: number
): Promise<boolean> => {
  if (!shortcode.trim()) return true
  const params: Record<string, string> = { shortcode }
  if (excludeId) params.exclude_id = String(excludeId)
  const res = await axios.get('oe/staff/validate-shortcode', { params })
  return res.data.available
}

// --- Name validation ---

export const validateName = async (
  name: string,
  excludeId?: number,
  parentId?: number
): Promise<boolean> => {
  if (!name.trim()) return true
  const params: Record<string, string> = { name }
  if (excludeId) params.exclude_id = String(excludeId)
  if (parentId) params.parent_id = String(parentId)
  const res = await axios.get('oe/staff/validate-name', { params })
  return res.data.available
}

/**
 * Parse API error response and return a Record<field, message>
 * for any field-level validation errors (e.g. shortcode, name).
 */
export const parseFieldErrors = (
  err: unknown
): Record<string, string> => {
  const axErr = err as import('axios').AxiosError<Record<string, string | string[]>>
  const data = axErr?.response?.data
  if (!data || typeof data !== 'object') return {}
  const result: Record<string, string> = {}
  for (const [key, val] of Object.entries(data)) {
    if (Array.isArray(val)) result[key] = val[0]
    else if (typeof val === 'string') result[key] = val
  }
  return result
}

// --- Atomic project + subprojects creation ---

type CreateProjectWithSubprojectsPayload = {
  project: {
    name: string
    shortcode?: string
    latitude: string
    longitude: string
    map_zoom: string
    logo: File
  }
  subprojects: {
    name: string
    shortcode: string
    latitude: string
    longitude: string
    logo?: File
    operation_type?: string
    cluster?: number | null
  }[]
}

type ProjectWithSubprojectsResponse = {
  project: IManagedProject
  subprojects: IManagedSubProject[]
}

export const createStaffProjectWithSubprojects = (
  body: CreateProjectWithSubprojectsPayload
): Promise<ProjectWithSubprojectsResponse> => {
  const payload = new FormData()
  payload.append('name', body.project.name)
  if (body.project.shortcode) payload.append('shortcode', body.project.shortcode)
  payload.append('latitude', body.project.latitude)
  payload.append('longitude', body.project.longitude)
  payload.append('map_zoom', body.project.map_zoom)
  payload.append('logo', body.project.logo)

  // Send subproject data as JSON (without logo files)
  const subsJson = body.subprojects.map((s) => ({
    name: s.name,
    shortcode: s.shortcode,
    latitude: s.latitude,
    longitude: s.longitude,
    ...(s.operation_type ? { operation_type: s.operation_type } : {}),
    ...(s.cluster ? { cluster: s.cluster } : {}),
  }))
  payload.append('subprojects', JSON.stringify(subsJson))

  // Attach subproject logos as separate files
  body.subprojects.forEach((s, idx) => {
    if (s.logo) payload.append(`subproject_logo_${idx}`, s.logo)
  })

  return axios.post('oe/staff/projects/manage-with-subprojects', payload).then((res) => res.data)
}

export const useCreateStaffProjectWithSubprojects = () => {
  return useMutation<
    ProjectWithSubprojectsResponse,
    Error,
    CreateProjectWithSubprojectsPayload
  >({
    mutationKey: ['staff-project-with-subprojects-create'],
    mutationFn: (params) => createStaffProjectWithSubprojects(params),
  })
}

// --- Atomic bulk subproject creation ---

type BulkCreateSubprojectsPayload = {
  parentId: number
  subprojects: {
    name: string
    shortcode: string
    latitude: string
    longitude: string
    logo?: File
    operation_type?: string
    cluster?: number | null
  }[]
}

type BulkCreateSubprojectsResponse = {
  subprojects: IManagedSubProject[]
}

export const bulkCreateStaffSubprojects = (
  body: BulkCreateSubprojectsPayload
): Promise<BulkCreateSubprojectsResponse> => {
  const payload = new FormData()

  const subsJson = body.subprojects.map((s) => ({
    name: s.name,
    shortcode: s.shortcode,
    latitude: s.latitude,
    longitude: s.longitude,
    ...(s.operation_type ? { operation_type: s.operation_type } : {}),
    ...(s.cluster ? { cluster: s.cluster } : {}),
  }))
  payload.append('subprojects', JSON.stringify(subsJson))

  body.subprojects.forEach((s, idx) => {
    if (s.logo) payload.append(`subproject_logo_${idx}`, s.logo)
  })

  return axios
    .post(`oe/staff/project/${body.parentId}/subprojects/bulk-create`, payload)
    .then((res) => res.data)
}

export const useBulkCreateStaffSubprojects = () => {
  return useMutation<
    BulkCreateSubprojectsResponse,
    Error,
    BulkCreateSubprojectsPayload
  >({
    mutationKey: ['staff-subproject-bulk-create'],
    mutationFn: (params) => bulkCreateStaffSubprojects(params),
  })
}

/**
 * Parse subproject_errors from the atomic API response.
 * Returns a map of subproject index -> Record<field, message>.
 */
export const parseSubprojectErrors = (
  err: unknown
): Record<number, Record<string, string>> => {
  const axErr = err as import('axios').AxiosError<{
    subproject_errors?: Record<string, Record<string, string[]>>
  }>
  const subErrors = axErr?.response?.data?.subproject_errors
  if (!subErrors || typeof subErrors !== 'object') return {}
  const result: Record<number, Record<string, string>> = {}
  for (const [idx, fields] of Object.entries(subErrors)) {
    const fieldMap: Record<string, string> = {}
    for (const [field, msgs] of Object.entries(fields)) {
      fieldMap[field] = Array.isArray(msgs) ? msgs[0] : String(msgs)
    }
    result[Number(idx)] = fieldMap
  }
  return result
}
